import type { CommentStatus, Ticket, TicketStatus } from "../api/types";

/**
 * TICKETS KANBAN — pure vocabulary + optimistic-move reducer (design
 * tickets-kanban §4.1 + §6). Framework-free so it unit-tests without a DOM; the
 * DnD wiring + card chrome live in `screens/kanban/*`. This owns only the column
 * set, the status→bucket grouping (with optimistic overrides), and the immutable
 * apply/revert reducer over the overrides map.
 *
 * The status vocabulary is split (design §1.2): all SIX statuses render as
 * columns so every ticket has a home, but only the FIVE human-settable statuses
 * (`CommentStatus`, types.ts) are drop targets. `orphaned` is the ONLY
 * system-derived status (the `PATCH /comments/:cid/status` validator rejects it —
 * a thread whose quote no longer resolves, never a manual choice), so its column
 * alone is drag-OUT-only. `awaiting_re_review` ("Re-review") IS settable: a
 * reviewer drops a note there to request another pass. This mirrors the server
 * `validStatus` allowlist exactly — the three surfaces (validator, columns,
 * dropdown) agree.
 */

export interface KanbanColumnDef {
  status: TicketStatus;
  /** True for the 5 human-settable statuses (drop targets); false for the lone
   *  system-derived `orphaned` status (drag-out-only). */
  droppable: boolean;
}

/** The 6 status columns in fixed display order (design §4.1). */
export const KANBAN_COLUMNS: readonly KanbanColumnDef[] = [
  { status: "open", droppable: true },
  { status: "awaiting_re_review", droppable: true },
  { status: "acted_on", droppable: true },
  { status: "resolved", droppable: true },
  { status: "pinned", droppable: true },
  { status: "orphaned", droppable: false },
] as const;

/** The settable subset — mirrors `CommentStatus` (types.ts) exactly and matches
 *  the server `validStatus` allowlist. A move target is always one of these. */
export const WRITABLE_STATUSES: readonly CommentStatus[] = [
  "open",
  "acted_on",
  "resolved",
  "awaiting_re_review",
  "pinned",
] as const;

/** Is `status`'s column a drop target (human-settable)? */
export function isDroppable(status: TicketStatus): boolean {
  return KANBAN_COLUMNS.some((c) => c.status === status && c.droppable);
}

/** Narrow a `TicketStatus` to the 5 settable `CommentStatus` values. */
export function isWritableStatus(
  status: TicketStatus,
): status is CommentStatus {
  return (WRITABLE_STATUSES as readonly string[]).includes(status);
}

/** Optimistic status overrides, keyed by ticket id (design §6). Never mutates
 *  the fetched ticket array — the board keeps this map alongside it. */
export type StatusOverrides = ReadonlyMap<number, CommentStatus>;

/** The status a card renders under: the optimistic override if in flight, else
 *  the server-fetched status. */
export function effectiveStatus(
  ticket: Ticket,
  overrides: StatusOverrides = EMPTY_OVERRIDES,
): TicketStatus {
  return overrides.get(ticket.id) ?? ticket.status;
}

const EMPTY_OVERRIDES: StatusOverrides = new Map();

/** Bin tickets into all SIX status buckets, preserving the API's created_at-desc
 *  order within each. Every column is present (empty columns → empty arrays) and
 *  the optimistic override (if any) decides a ticket's bucket. */
export function groupByStatus(
  tickets: Ticket[],
  overrides: StatusOverrides = EMPTY_OVERRIDES,
): Record<TicketStatus, Ticket[]> {
  const groups: Record<TicketStatus, Ticket[]> = {
    open: [],
    resolved: [],
    acted_on: [],
    awaiting_re_review: [],
    pinned: [],
    orphaned: [],
  };
  for (const t of tickets) groups[effectiveStatus(t, overrides)].push(t);
  return groups;
}

/**
 * Apply an optimistic move (design §6.1): returns a NEW overrides map pinning
 * `ticketId` to `target`. A move to the ticket's CURRENT effective status is a
 * no-op and returns the SAME map reference (so the caller can skip the network
 * write). Immutable — never mutates the input map.
 */
export function applyMove(
  overrides: StatusOverrides,
  ticketId: number,
  target: CommentStatus,
  serverStatus: TicketStatus,
): StatusOverrides {
  const effective = overrides.get(ticketId) ?? serverStatus;
  if (effective === target) return overrides;
  const next = new Map(overrides);
  next.set(ticketId, target);
  return next;
}

/**
 * Revert an in-flight move (design §6.2 error path): drops the override so the
 * card snaps back to its server column. Returns the SAME map reference when the
 * ticket had no override. Immutable.
 */
export function revertMove(
  overrides: StatusOverrides,
  ticketId: number,
): StatusOverrides {
  if (!overrides.has(ticketId)) return overrides;
  const next = new Map(overrides);
  next.delete(ticketId);
  return next;
}
